// lib/dailyPrograms.js
// R9 운동 프로그램 탭(날짜·타입별 프로그램)의 폼·주간 목록 계산. 계약: docs/architecture/record-daily-design.md
//   0절(2026-10-12 사장님 결정) 2·3·7·12·14·16, 1.2·1.3, 2.2·2.3
//   - 날짜와 타입을 골라 안내 문장(필수)·프로그램 내용(자유 글)·카디오 포커스(필수)·웨이트 포커스(선택)를 쓴다.
//     같은 날짜·타입의 여러 시간대가 함께 쓴다. 포커스는 R10에서 두 칸이 됐다(16, DECISIONS R10-1·R10-4).
//   - 회원 앱 일간 리포트 맨 위에 입력한 그대로(글자만, 줄바꿈 그대로) 보인다. 쓰지 않으면 타입 기본 문장이 보인다(3).
//   - 그 날짜·타입을 처음 저장할 때 한 번 그 기록들의 총평을 다시 만든다. 그 뒤 고쳐도 총평은 그대로다(14). 처음 저장 때의
//     카디오 포커스가 총평·사실·리포트에 남으므로 처음 저장 전에 확인 창을 띄운다(firstSaveConfirmText, DECISIONS R10-5·R10-6).
//   API: GET /v1/admin/daily-programs?from=&to=, GET·PUT /v1/admin/daily-programs/{date}/{type} (lib/classAdminApi.js)
//        PUT 본문 { headline, content, cardio_focus, weight_focus }. R9 키 focus는 보내지 않는다(서버가 422로 거절한다)
// 타입 표는 lib/classTypes.js의 CLASS_TYPES를 인자로 받는다(이 파일은 import가 없다: scripts/test-class-info.mjs와
// scripts/test-daily-programs.mjs가 node로 그대로 불러 시험한다).

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// 카디오 포커스 값(cardio_focus_enum 순서, classTypes.CARDIO_FOCUSES와 같다)과 웨이트 포커스 값(class_focus_enum 순서,
// classTypes.WEIGHT_FOCUSES와 같다). 이 파일은 import가 없어 값만 따로 둔다(시험이 두 표가 같은지 본다)
export const CARDIO_VALUES = [
  "sprint",
  "vo2max",
  "threshold",
  "circuit",
  "aerobic",
];
export const WEIGHT_VALUES = ["power", "strength", "hypertrophy", "endurance"];
const DOW = ["일", "월", "화", "수", "목", "금", "토"];

// 서버 검증과 같은 길이 제한(글자 수 = 유니코드 코드 포인트, 서버 Go의 rune 수와 같다)
export const HEADLINE_MAX = 200;
export const CONTENT_MAX = 5000;

function parseYmd(ymd) {
  const match = YMD_RE.exec(String(ymd || ""));
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return date;
}

function toYmd(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

export function isYmd(value) {
  return !!parseYmd(value);
}

// 글자 수(코드 포인트). 이모지 하나도 1자
export function charCount(value) {
  return [...String(value == null ? "" : value)].length;
}

// "YYYY-MM-DD" + n일. 날짜가 아니면 ""
export function addDaysYmd(ymd, n) {
  const date = parseYmd(ymd);
  if (!date) return "";
  date.setUTCDate(date.getUTCDate() + n);
  return toYmd(date);
}

// 그 날짜가 든 주의 월~일 7일. 날짜가 아니면 []
export function weekDates(ymd) {
  const date = parseYmd(ymd);
  if (!date) return [];
  const dow = date.getUTCDay(); // 0=일
  const monday = addDaysYmd(toYmd(date), dow === 0 ? -6 : 1 - dow);
  return Array.from({ length: 7 }, (_, i) => addDaysYmd(monday, i));
}

// "10월 13일 (월)"
export function formatDayLabel(ymd) {
  const date = parseYmd(ymd);
  if (!date) return "";
  return `${date.getUTCMonth() + 1}월 ${date.getUTCDate()}일 (${
    DOW[date.getUTCDay()]
  })`;
}

// 주 이름 "10월 13일 ~ 10월 19일"
export function formatWeekRange(dates) {
  if (!Array.isArray(dates) || dates.length === 0) return "";
  const first = parseYmd(dates[0]);
  const last = parseYmd(dates[dates.length - 1]);
  if (!first || !last) return "";
  return `${first.getUTCMonth() + 1}월 ${first.getUTCDate()}일 ~ ${
    last.getUTCMonth() + 1
  }월 ${last.getUTCDate()}일`;
}

// 서버 시각(updated_at) → "10월 12일 14:05"(서울). 시각이 아니면 ""
export function formatSavedAt(value) {
  if (!value) return "";
  const t = new Date(String(value));
  if (Number.isNaN(t.getTime())) return "";
  const seoul = new Date(t.getTime() + 9 * 60 * 60 * 1000);
  return `${seoul.getUTCMonth() + 1}월 ${seoul.getUTCDate()}일 ${String(
    seoul.getUTCHours()
  ).padStart(2, "0")}:${String(seoul.getUTCMinutes()).padStart(2, "0")}`;
}

function typeValues(types) {
  return Array.isArray(types) ? types.map((t) => t && t.value) : [];
}

// 예전 타입(R9 전 Hybrid·Core·Cardio). 새 수업에는 쓰지 않고 지난·이미 만든 수업에만 남는다
const LEGACY_TYPES = ["Hybrid", "Core", "Cardio"];

// 수업(스케줄 항목)이 쓰는 운동 프로그램 타입. 서버 models.EffectiveClassType과 같은 규칙이다(DECISIONS R9-6):
// 예전 타입인데 제목이 새 타입 표시 이름(앞뒤 공백·대소문자 무시)이면 그 새 타입, 아니면 type 그대로.
// D22(2026-10-03)부터 운영에서 만든 수업은 type Cardio + 요일 이름 제목이라, 그 날짜의 그 새 타입 프로그램을 회원 앱이 읽는다.
export function effectiveTypeOf(cls, types) {
  const raw = cls && cls.type != null ? String(cls.type) : "";
  if (!LEGACY_TYPES.includes(raw)) return raw;
  const title =
    cls && typeof cls.title === "string" ? cls.title.trim().toLowerCase() : "";
  if (!title) return raw;
  const hit = (Array.isArray(types) ? types : []).find(
    (t) => t && typeof t.label === "string" && t.label.toLowerCase() === title
  );
  return hit ? hit.value : raw;
}

function labelOf(types, value, serverLabel) {
  if (typeof serverLabel === "string" && serverLabel.trim()) {
    return serverLabel.trim();
  }
  const t = Array.isArray(types)
    ? types.find((x) => x && x.value === value)
    : null;
  return t ? t.label : value == null ? "" : String(value);
}

// 타입 기본 문장(사장님 결정 3, 구현 기본값). 회원 앱이 프로그램이 없을 때 보이는 문장과 같아야 한다(서버가 만든다)
export function defaultHeadline(label) {
  return `오늘은 ${label} 수업이었어요.`;
}

// 빈 폼(아직 쓰지 않은 날짜·타입). 카디오 포커스는 비워 두고 직접 고른다
export function emptyProgramForm(date, type) {
  return {
    date: isYmd(date) ? String(date) : "",
    type: type ? String(type) : "",
    headline: "",
    content: "",
    cardioFocus: "",
    weightFocus: "",
    exists: false,
    updatedAt: "",
  };
}

// 서버 항목 → 폼. item이 없으면(404) 빈 폼. 포커스는 값 표에 있을 때만(모르는 값·null은 ""). 카디오 포커스가 null인 항목은
// R10 전에 저장한 개발 DB 행뿐이고, 고쳐 저장하려면 카디오 포커스를 골라야 한다
export function programFormFrom(item, date, type) {
  if (!item || typeof item !== "object") return emptyProgramForm(date, type);
  return {
    date: isYmd(item.date)
      ? String(item.date)
      : isYmd(date)
      ? String(date)
      : "",
    type: item.type ? String(item.type) : type ? String(type) : "",
    headline: typeof item.headline === "string" ? item.headline : "",
    content: typeof item.content === "string" ? item.content : "",
    cardioFocus: CARDIO_VALUES.includes(item.cardio_focus)
      ? item.cardio_focus
      : "",
    weightFocus: WEIGHT_VALUES.includes(item.weight_focus)
      ? item.weight_focus
      : "",
    exists: true,
    updatedAt: item.updated_at || item.created_at || "",
  };
}

// 폼 검사. types = CLASS_TYPES(고를 수 있는 타입). { ok, errors: { date?, type?, headline?, content?, cardioFocus? }, message }
// 카디오 포커스는 필수(서버 422 cardio_focus와 같다, DECISIONS R10-1). 웨이트 포커스는 선택 상자라 틀릴 값이 없다
// (모르는 값은 본문에서 null)
export function validateProgramForm(form, types) {
  const f = form && typeof form === "object" ? form : {};
  const errors = {};
  if (!isYmd(f.date)) errors.date = "날짜를 골라 주세요.";
  if (!typeValues(types).includes(f.type)) errors.type = "타입을 골라 주세요.";
  const headline = String(f.headline || "").trim();
  if (!headline) errors.headline = "안내 문장을 입력해 주세요.";
  else if (charCount(headline) > HEADLINE_MAX) {
    errors.headline = `안내 문장은 ${HEADLINE_MAX}자까지 쓸 수 있어요.`;
  }
  if (charCount(f.content) > CONTENT_MAX) {
    errors.content = `프로그램 내용은 ${CONTENT_MAX}자까지 쓸 수 있어요.`;
  }
  if (!CARDIO_VALUES.includes(f.cardioFocus)) {
    errors.cardioFocus = "카디오 포커스를 골라 주세요.";
  }
  const order = ["date", "type", "headline", "content", "cardioFocus"];
  const first = order.find((k) => errors[k]);
  return { ok: !first, errors, message: first ? errors[first] : "" };
}

// PUT 본문 { headline, content, cardio_focus, weight_focus }. 안내 문장은 앞뒤 공백을 뺀다. 내용은 입력한 그대로(공백뿐이면 "").
// 카디오 포커스는 값 표에 있는 값(고르지 않았으면 "": 화면 검사가 저장을 막고, 그래도 가면 서버가 422로 거절한다).
// 웨이트 포커스는 없으면 null(PUT은 전체 교체라 지운다). R9 키 focus는 넣지 않는다(서버가 422 focus로 거절한다)
export function programRequestBody(form) {
  const f = form && typeof form === "object" ? form : {};
  const content = typeof f.content === "string" ? f.content : "";
  return {
    headline: String(f.headline || "").trim(),
    content: content.trim() ? content : "",
    cardio_focus: CARDIO_VALUES.includes(f.cardioFocus) ? f.cardioFocus : "",
    weight_focus: WEIGHT_VALUES.includes(f.weightFocus) ? f.weightFocus : null,
  };
}

// 저장한 값(saved = 마지막으로 받은 폼)과 지금 폼이 다른가(본문 네 칸). 새 프로그램이면 무엇이든 쓴 게 있으면 true
export function hasProgramChanges(saved, form) {
  const a = programRequestBody(saved);
  const b = programRequestBody(form);
  return (
    a.headline !== b.headline ||
    a.content !== b.content ||
    a.cardio_focus !== b.cardio_focus ||
    a.weight_focus !== b.weight_focus
  );
}

// 처음 저장(그 날짜·타입에 아직 프로그램이 없음) 전 확인 창 문구. cardioLabel = 고른 카디오 포커스 표시 이름("무산소 역치").
// 서버는 처음 저장(201)일 때만 그 날짜·타입 기록의 오늘의 총평과 사실을 그때의 포커스로 한 번 다시 만들고, 그 뒤 고쳐도
// 이미 만든 총평과 그 사실을 모으는 리포트 문장은 바뀌지 않는다(사장님 결정 14, DECISIONS R10-5·R10-6).
// 기록이 아직 없는 첫 저장(수업 전에 쓴 프로그램)에는 다시 만들 것이 없어서 "이미 있는"과 괄호로 그때를 밝힌다(R9 리뷰 4차 saveResultText와 같은 까닭)
export function firstSaveConfirmText(cardioLabel) {
  const label = typeof cardioLabel === "string" ? cardioLabel.trim() : "";
  const basis = label ? `'${label}' 기준으로 ` : "";
  return `처음 저장하면 이 날짜·타입에 이미 있는 수업 기록의 오늘의 총평을 ${basis}한 번 다시 만들어요(기록이 없으면 다시 만들 것이 없어요). 그 뒤에 포커스를 고쳐도 이미 만든 총평과 리포트 문장은 바뀌지 않아요. 저장할까요?`;
}

// 저장 결과 문구. created = 201(이 날짜·타입을 처음 저장), regenerated = 서버가 다시 만든 오늘의 총평 수(모르면 null).
// 서버가 실제로 다시 만든 만큼만 말한다(R9 리뷰 4차): 코치가 수업 전에 프로그램을 먼저 쓰면 기록이 아직 없어 다시 만든 것이 없다.
// 그때 "다시 만들었어요"라고 하면 사실이 아니고, 한 번뿐인 다시 만들기를 써 버렸다고 오해한다. 그 뒤 들어오는 기록은 처음부터
// 이 프로그램으로 총평을 만든다(기록 저장·세션 상세 첫 조회 때)
export function saveResultText(created, regenerated = null) {
  if (!created) return "저장했어요. 이미 만든 오늘의 총평은 바뀌지 않아요.";
  if (regenerated === 0) {
    return "저장했어요. 이 날짜·타입 수업 기록이 아직 없어 다시 만든 총평은 없어요. 앞으로 들어오는 기록의 총평은 이 프로그램으로 만들어요.";
  }
  if (Number.isInteger(regenerated) && regenerated > 0) {
    return `저장했어요. 이 날짜·타입 수업 기록 ${regenerated}개의 오늘의 총평을 프로그램에 맞춰 한 번 다시 만들었어요.`;
  }
  return "저장했어요. 이 날짜·타입에 수업 기록이 있었다면 그 오늘의 총평을 프로그램에 맞춰 한 번 다시 만들었어요.";
}

// 주간 목록. dates = weekDates, items = 프로그램 목록 응답 items, classesByDate = { "YYYY-MM-DD": 스케줄 항목[] }
// (못 받은 날짜는 키가 없다), types = CLASS_TYPES.
// 날짜마다 그 날 수업이 있는 타입과 프로그램이 있는 타입을 모아 타입 표 순서로 보인다(표에 없는 타입은 뒤에 값 순서로).
// 수업은 effectiveTypeOf로 센다: 예전 타입 + 새 타입 이름 제목(D22) 수업은 그 새 타입 줄에 들어간다(서버와 같은 프로그램).
// status: written(프로그램 있음) | missing(수업은 있는데 아직 안 씀) | legacy(새 타입 이름이 아닌 예전 타입 수업만 있음:
// 프로그램을 쓸 수 없고 회원 앱은 기본 문장을 보인다, 안 쓴 수에 넣지 않음). classCount = 그 날짜·타입 수업 수(모르면 null).
// cardioFocus = 쓴 프로그램의 카디오 포커스 값(목록에 "Pull & Cardio · 무산소 역치"로 보인다). 프로그램이 없거나 카디오 포커스가
// null(R10 전 개발 DB 행)·모르는 값이면 ""(쓴 줄은 "카디오 포커스 없음"으로 보인다)
export function programWeekRows(dates, items, classesByDate, types) {
  const order = typeValues(types);
  const rank = (v) => {
    const i = order.indexOf(v);
    return i >= 0 ? i : order.length;
  };
  const list = Array.isArray(items) ? items : [];
  const byDate =
    classesByDate && typeof classesByDate === "object" ? classesByDate : {};
  return (Array.isArray(dates) ? dates : []).map((date) => {
    const known = Object.prototype.hasOwnProperty.call(byDate, date);
    const classes = known && Array.isArray(byDate[date]) ? byDate[date] : [];
    const counts = {};
    const classLabels = {};
    for (const c of classes) {
      if (!c || !c.type) continue;
      const key = effectiveTypeOf(c, types);
      counts[key] = (counts[key] || 0) + 1;
      // 서버 표시 이름은 그 수업 type의 이름이라 새 타입으로 센 예전 수업에는 쓰지 않는다(타입 표 이름)
      if (!classLabels[key] && key === c.type)
        classLabels[key] = c.type_label || c.typeLabel;
    }
    const programs = {};
    for (const p of list) {
      if (p && p.date === date && p.type) programs[p.type] = p;
    }
    const keys = [
      ...new Set([...Object.keys(counts), ...Object.keys(programs)]),
    ];
    keys.sort((a, b) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0));
    return {
      date,
      label: formatDayLabel(date),
      classesKnown: known,
      entries: keys.map((type) => {
        const program = programs[type] || null;
        return {
          type,
          label: labelOf(
            types,
            type,
            (program && program.type_label) || classLabels[type]
          ),
          program,
          headline: program ? String(program.headline || "") : "",
          cardioFocus:
            program && CARDIO_VALUES.includes(program.cardio_focus)
              ? program.cardio_focus
              : "",
          classCount: known ? counts[type] || 0 : null,
          status: program
            ? "written"
            : LEGACY_TYPES.includes(type)
            ? "legacy"
            : "missing",
        };
      }),
    };
  });
}

// 주간 목록의 아직 안 쓴 수(수업이 있는데 프로그램이 없는 날짜·타입)
export function missingCount(rows) {
  return (Array.isArray(rows) ? rows : []).reduce(
    (n, r) => n + r.entries.filter((e) => e.status === "missing").length,
    0
  );
}
